import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from '../../utils/toast';
import { postJson } from '../../utils/apiClient';

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = await postJson('login.php', {
        email: formData.email,
        password: formData.password
      });

      if (!data?.data?.token || !data?.data?.user) {
        throw new Error('Login response is missing token or user data.');
      }

      // Store token and user data (includes is_admin and user_type)
      localStorage.setItem('token', data.data.token);
      localStorage.setItem('user', JSON.stringify(data.data.user));
      localStorage.setItem('userType', data.data.user.user_type || 'individual');

      if (onLogin) {
        onLogin(data.data);
      }
      toast.success('Signed in successfully');
      
      // Navigate based on user role and type
      if (data.data.user.is_admin) {
        navigate('/admin');
      } else if (data.data.user.role === 'employee' || data.data.user.user_type === 'employee') {
        navigate('/dashboards');
      } else if (data.data.user.role === 'sub_company' || data.data.user.user_type === 'sub_company') {
        navigate('/company/client-dashboard');
      } else if (data.data.user.user_type === 'company') {
        navigate('/company/dashboard');
      } else {
        navigate('/dashboard');
      }

    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
      toast.error(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex items-center justify-center p-4">
      <div className="max-w-6xl w-full bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex flex-col md:flex-row">
          {/* Left Column - Login Form */}
          <div className="md:w-1/2 p-8 md:p-10">
            <div className="mb-6">
              <Link to="/" className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-blue-400 rounded-md flex items-center justify-center">
                  <span className="text-white font-bold text-lg">C</span>
                </div>
                <span className="text-2xl font-bold">Talencar</span>
              </Link>
              <h1 className="text-3xl font-bold text-gray-900 mt-4">Welcome Back</h1>
              <p className="text-gray-600 mt-2">Sign in to your account to continue</p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400">✉️</span>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="you@example.com"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400">🔒</span>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter your password"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="rememberMe"
                    name="rememberMe"
                    checked={formData.rememberMe}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                    disabled={loading}
                  />
                  <label htmlFor="rememberMe" className="ml-2 text-sm text-gray-600">
                    Remember me
                  </label>
                </div>
                <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-700">
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                className="w-full px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-400 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-blue-500 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </button>

              {/* Social Login */}
              {/* <div className="mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">Or continue with</span>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    className="w-full inline-flex justify-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    disabled={loading}
                  >
                    <span>🔵</span>
                    <span className="ml-2">Google</span>
                  </button>
                  <button
                    type="button"
                    className="w-full inline-flex justify-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    disabled={loading}
                  >
                    <span>🔵</span>
                    <span className="ml-2">LinkedIn</span>
                  </button>
                </div>
              </div> */}
            </form>

            <div className="mt-8 pt-6 border-t border-gray-200 text-center">
              <p className="text-gray-600">
                Don't have an account?{' '}
                <Link to="/signup" className="text-blue-600 font-semibold hover:text-blue-700">
                  Sign up
                </Link>
              </p>
            </div>
          </div>

          {/* Right Column - Image & Features */}
          <div className="md:w-1/2 bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600 p-8 md:p-10 relative hidden md:block">
            <div className="relative z-10 text-white h-full flex flex-col">
              <div className="flex-1">
                <h2 className="text-3xl font-bold mb-4">Your Career Journey Starts Here</h2>
                <p className="text-lg text-blue-100 mb-8">
                  Join thousands of professionals who found their dream jobs through Talencar
                </p>
                
                <div className="space-y-5">
                  <div className="flex items-center space-x-4">
                    <span className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center text-xl">🎯</span>
                    <div>
                      <h3 className="font-semibold text-base">Personalized Job Matches</h3>
                      <p className="text-blue-100 text-sm">Get recommendations based on your skills</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center text-xl">📊</span>
                    <div>
                      <h3 className="font-semibold text-base">Profile Analytics</h3>
                      <p className="text-blue-100 text-sm">Track who views your profile</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center text-xl">⚡</span>
                    <div>
                      <h3 className="font-semibold text-base">Quick Apply</h3>
                      <p className="text-blue-100 text-sm">Apply to jobs with one click</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center text-xl">🏢</span>
                    <div>
                      <h3 className="font-semibold text-base">For Companies</h3>
                      <p className="text-blue-100 text-sm">Post jobs and find top talent</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-3">
                <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm text-center">
                  <span className="text-2xl font-bold">10k+</span>
                  <p className="text-blue-100 text-xs">Jobs Posted</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm text-center">
                  <span className="text-2xl font-bold">5k+</span>
                  <p className="text-blue-100 text-xs">Companies</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm text-center">
                  <span className="text-2xl font-bold">95%</span>
                  <p className="text-blue-100 text-xs">Success Rate</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm text-center">
                  <span className="text-2xl font-bold">24/7</span>
                  <p className="text-blue-100 text-xs">Support</p>
                </div>
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-400/20 rounded-full blur-3xl"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
